/**
 * A tone when something arrives.
 *
 * In the shell the sound is played by Rust. A WebView2 that has never
 * seen a click will not play audio at all, and a counter PC can go a
 * whole day without one, so asking the page to do it would give a
 * board that is silent exactly where it matters most.
 *
 * In a browser there is no such helper, so we synthesise the same two
 * notes. That works once the person has interacted with the page,
 * which on the chat platform they always have.
 */
const invoker = (): ((cmd: string) => Promise<unknown>) | null => {
  const t = (window as unknown as {
    __TAURI__?: {
      core?: { invoke?: (c: string) => Promise<unknown> };
      invoke?: (c: string) => Promise<unknown>;
    };
  }).__TAURI__;
  return t?.core?.invoke ?? t?.invoke ?? null;
};

let ctx: AudioContext | null = null;

/** G5 then C6: short, rising, and not the sound of an error. */
const NOTES = [784, 1046.5];

const synth = (): void => {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    NOTES.forEach((freq, i) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const at = now + i * 0.14;
      // Ramped, not switched: a square edge on a sine is a click.
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.18, at + 0.012);
      gain.gain.linearRampToValueAtTime(0, at + 0.13);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(at);
      osc.stop(at + 0.15);
    });
  } catch {
    // No audio device, or a policy that refuses. Not worth an error
    // on a board whose job is to show notices.
  }
};

let last = 0;

/**
 * At most one tone every two seconds. Five notices arriving together
 * should sound like an arrival, not an alarm, and a counter screen
 * that shrieks is a counter screen somebody mutes.
 */
export const chime = (): void => {
  const now = Date.now();
  if (now - last < 2000) return;
  last = now;

  const invoke = invoker();
  if (invoke) {
    void Promise.resolve(invoke('chime')).catch(synth);
    return;
  }
  synth();
};

/**
 * Sending something of our own should not ring our own bell. The
 * server echoes what we post back down the socket, and there is no
 * sender on the event to tell the two apart, so the send path says
 * so directly. Reuses the throttle window above.
 */
export const suppress = (): void => {
  last = Date.now();
};
